/**
 * @module  js/services/crmUtils.js
 * @summary Determines and caches the current user's linked CRM provider.
 *
 * @description
 * Reads Firestore config docs to detect whether the user is connected to HubSpot or Salesforce.
 * Memoises the result in-memory and in localStorage to minimise network calls on subsequent checks.
 *
 * ## Key Features
 * – Dual-provider detection via parallel Firestore look-ups
 * – Per-session memoisation plus persistent localStorage caching
 * – Helper to clear cached state for reliable logout or provider switches
 *
 * ## Public API
 * – `getCurrentCRM(userId, opts)` — resolves `"salesforce" | "hubspot" | "local"` with memoisation
 * – `clearCurrentCRM()` — resets in-memory and (optionally) localStorage caches
 *
 * ## Dependencies
 * { firebase/firestore, firebase-service.js }
 *
 * @author   Auto-Generated
 * @version  1.0.0
 * @since    2025-04-28
 */

import { getDoc } from 'https://www.gstatic.com/firebasejs/9.17.2/firebase-firestore.js';
import { docWithTenant } from '../utils/firestoreHelpers.js';

/**
 * Cached CRM entries older than this require a Firestore revalidation.
 * @type {number}
 */
const CRM_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes

/**
 * Read a cached CRM entry (if present) from localStorage.
 * Gracefully handles legacy string values without metadata.
 *
 * @param {string} cacheKey
 * @returns {{ value: string | null, timestamp: number }}
 */
function readCachedCRM(cacheKey) {
  try {
    const raw = localStorage.getItem(cacheKey);
    if (!raw) {
      return { value: null, timestamp: 0 };
    }
    if (raw.trim().startsWith('{')) {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed.value === 'string') {
        const ts = Number(parsed.timestamp);
        return { value: parsed.value, timestamp: Number.isFinite(ts) ? ts : 0 };
      }
    } else if (typeof raw === 'string') {
      return { value: raw, timestamp: 0 };
    }
  } catch (err) {
    console.warn('Unable to parse cached CRM entry', err);
  }
  return { value: null, timestamp: 0 };
}

/**
 * Persist the CRM cache entry with a fresh timestamp.
 * Swallows any storage errors (Safari private mode, quota, etc.).
 *
 * @param {string} cacheKey
 * @param {string} value
 */
function persistCRMCache(cacheKey, value) {
  try {
    localStorage.setItem(
      cacheKey,
      JSON.stringify({ value, timestamp: Date.now() })
    );
  } catch {
    /* noop */
  }
}

/**
 * Determine whether a Salesforce config doc contains a completed OAuth link.
 *
 * @param {Record<string, unknown>} data
 * @returns {boolean}
 */
function hasSalesforceConnection(data = {}) {
  return Boolean(
    data.instanceUrl &&
      (data.sfOwnerId || data.salesforceUserId) &&
      (data.accessToken || data.refreshToken)
  );
}

/**
 * Determine whether a HubSpot config doc contains a completed OAuth link.
 *
 * @param {Record<string, unknown>} data
 * @returns {boolean}
 */
function hasHubSpotConnection(data = {}) {
  return Boolean(
    data.hubspotOwnerId && (data.accessToken || data.refreshToken)
  );
}

let currentCRM = null;
let currentCRMPromise = null;

/**
 * getCurrentCRM - Memoizes and returns the CRM type for a given user.
 * @param {string} userId - The UID of the authenticated user.
 * @param {{ authService?: any, forceRefresh?: boolean }} [options]
 * @returns {Promise<string>} - Resolves to "salesforce", "hubspot" or "local".
 */
export async function getCurrentCRM(
  userId,
  { authService, forceRefresh = false } = {}
) {
  // fall back to global DI if caller passed nothing
  if (!authService) {
    const di = (await import('../bootstrap/app.js')).default;
    authService = di.auth;
  }

  // Use memoization for the current page load unless caller requested a forced refresh.
  if (!forceRefresh) {
    if (currentCRM) {
      return currentCRM;
    }
    if (currentCRMPromise) {
      return await currentCRMPromise;
    }
  }

  const cacheKey = `crm_${userId}`;
  const { value: cachedCRM, timestamp: cachedAt } = readCachedCRM(cacheKey);
  const cacheFresh =
    Boolean(cachedCRM) &&
    cachedAt > 0 &&
    Date.now() - cachedAt < CRM_CACHE_TTL_MS;

  if (!forceRefresh && cachedCRM && cacheFresh) {
    currentCRM = cachedCRM;
    return cachedCRM;
  }

  const fetchPromise = (async () => {
    const user = authService.currentUser || (await authService.waitForLogin());
    const tenantId = user?.tenantId;
    if (!tenantId) {
      console.error('Missing tenant ID');
      throw new Error('Missing tenant ID');
    }
    // Always read general flags to enforce CRM-less mode.
    const generalRef = docWithTenant(tenantId, `AppConfig/general`);
    const generalSnap = await getDoc(generalRef);
    const g = generalSnap.exists() ? generalSnap.data() : {};
    if (g.skipCrmCreates === true && g.crmLessImport === true) {
      currentCRM = 'local';
      persistCRMCache(cacheKey, 'local');
      return 'local';
    }

    // No CRM-less override → detect provider normally.
    const sfDocRef = docWithTenant(tenantId, `SalesforceConfig/${userId}`);
    const hsDocRef = docWithTenant(tenantId, `HubSpotConfig/${userId}`);
    const [sfDocSnap, hsDocSnap] = await Promise.all([
      getDoc(sfDocRef),
      getDoc(hsDocRef),
    ]);

    const sfData = sfDocSnap.exists() ? sfDocSnap.data() || {} : {};
    const hsData = hsDocSnap.exists() ? hsDocSnap.data() || {} : {};

    let crm;
    if (hasSalesforceConnection(sfData)) {
      crm = 'salesforce';
    } else if (hasHubSpotConnection(hsData)) {
      crm = 'hubspot';
    } else {
      crm = 'local'; // no CRM configured
    }
    currentCRM = crm;
    // Save to localStorage for future page loads
    persistCRMCache(cacheKey, crm);
    return crm;
  })();
  currentCRMPromise = fetchPromise;
  try {
    return await fetchPromise;
  } finally {
    if (currentCRMPromise === fetchPromise) {
      currentCRMPromise = null;
    }
  }
}

export function clearCurrentCRM() {
  currentCRM = null;
  currentCRMPromise = null;
  // Best-effort wipe for all cached CRM keys for this origin.
  try {
    Object.keys(localStorage)
      .filter((k) => k.startsWith('crm_'))
      .forEach((k) => localStorage.removeItem(k));
  } catch {
    /* noop */
  }
}
